import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import { WarningAmber } from '@mui/icons-material';

const CancellationModal = ({ open, onClose, session, userRole, onConfirm }) => {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Determine other person and session details
  const otherPerson = useMemo(() => {
    if (!session) return null;
    return userRole === 'Seeker' ? session.professionalId : session.seekerId;
  }, [session, userRole]);

  // Calculate time until session
  const { sessionDate, hoursUntilSession, withinDeadline } = useMemo(() => {
    if (!session) return { sessionDate: new Date(), hoursUntilSession: 0, withinDeadline: false };

    const date = new Date(session.datetime || session.sessionDetails?.proposedDate);
    const now = new Date();
    const hours = (date - now) / (1000 * 60 * 60);

    // Seekers need 24hrs, professionals can cancel anytime
    const deadline = userRole === 'Seeker' ? hours >= 24 : true;

    return {
      sessionDate: date,
      hoursUntilSession: hours,
      withinDeadline: deadline
    };
  }, [session, userRole]);

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);

    try {
      await onConfirm(session._id || session.id, reason);
      // Reset state
      setReason('');
      setError(null);
      onClose();
    } catch (err) {
      const errorMessage = err.response?.data?.error?.message || err.message || 'Failed to cancel session';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setReason('');
      setError(null);
      onClose();
    }
  };

  if (!session) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <WarningAmber color="warning" />
          <Typography variant="h6">Cancel Session</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {!withinDeadline ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="bold">
              Cancellation Deadline Passed
            </Typography>
            <Typography variant="body2">
              Sessions must be cancelled at least 24 hours before the start time.
              Your session starts in {Math.floor(hoursUntilSession)} hours and{' '}
              {Math.floor((hoursUntilSession % 1) * 60)} minutes.
            </Typography>
          </Alert>
        ) : (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold">
                Important: Communicate First
              </Typography>
              <Typography variant="body2">
                Please make sure you've communicated with{' '}
                <strong>
                  {otherPerson?.firstName || otherPerson?.name?.split(' ')[0] || 'the other user'}
                  {' '}
                  {otherPerson?.lastName || otherPerson?.name?.split(' ').slice(1).join(' ') || ''}
                </strong>{' '}
                about this cancellation beforehand.
              </Typography>
            </Alert>

            {userRole === 'Seeker' && session.price && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  You will receive a <strong>full refund</strong> of{' '}
                  <strong>£{((session.price || 0) / 100).toFixed(2)}</strong> within 5-10 business days.
                </Typography>
              </Alert>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Session Details:
              <br />
              📅 {sessionDate.toLocaleDateString('en-GB', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
              <br />
              ⏰ {sessionDate.toLocaleTimeString('en-GB', {
                hour: '2-digit',
                minute: '2-digit'
              })}
              <br />
              ⏱️ Duration: {session.duration || session.sessionDetails?.duration || 30} minutes
            </Typography>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Cancellation Reason (Optional)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Let us know why you're cancelling..."
              variant="outlined"
              inputProps={{ maxLength: 500 }}
              helperText={`${reason.length}/500 characters`}
              disabled={loading}
            />

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          {withinDeadline ? "Don't Cancel" : 'Close'}
        </Button>
        {withinDeadline && (
          <Button
            onClick={handleConfirm}
            color="error"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {loading ? 'Cancelling...' : 'Confirm Cancellation'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CancellationModal;
